// FROM-list + JOIN parser: walks the byte range of a FROM clause and splits
// it into FromItem[] (comma-separated targets) + JoinClause[] (JOIN segments).
// Returns flat lists; v1 does not encode "this JOIN attaches to that FROM
// item" — VQE-Edit composes joins onto the first reachable target.
//
// PRD: Ralph/SemanticResolverEdit/PRD.md § Slice: Query Structure Extractor — SELECT.

import type { ParseOptions } from '../../sql-grammar/model';
import { rangeFromOffsets } from '../../sql-grammar/document/lineIndex';
import { skipContextFor } from '../classify/tokenSkip';
import { readIdentifier, readQualifiedName, foldIdentifier } from '../scope/identifiers';
import { computeCteBindings } from '../scope/cteScope';
import type { SqlRange } from '../../sql-grammar/model';
import type { FromItem, JoinClause, JoinKind, AliasBinding, BooleanFragment, CteBinding } from '../types';

import { extractQueryStructure } from './extractQueryStructure';
import { buildBooleanFragment } from './extractQueryStructure.predicates';
import { skipParen } from './scanner';
import type { SelectExtractCtx } from './extractQueryStructure.select';

const JOIN_PREFIXES: ReadonlySet<string> = new Set([
  'INNER', 'LEFT', 'RIGHT', 'FULL', 'OUTER', 'CROSS', 'NATURAL', 'LATERAL', 'STRAIGHT_JOIN',
]);

export interface FromAndJoins {
  readonly fromItems: readonly FromItem[];
  readonly joins: readonly JoinClause[];
}

// eslint-disable-next-line max-lines-per-function, complexity
export function parseFromAndJoins(
  ctx: SelectExtractCtx,
  start: number,
  end: number,
): FromAndJoins {
  const { source, options } = ctx;
  const fromItems: FromItem[] = [];
  const joins: JoinClause[] = [];
  const supportsHashComment = skipContextFor(options).supportsHashComment;
  let i = skipWsAndComments(source, start, end, supportsHashComment);
  let prefixWords: string[] = [];

  while (i < end) {
    i = skipWsAndComments(source, i, end, supportsHashComment);
    if (i >= end) break;
    // Look at next identifier/keyword.
    const tok = readIdentifier(source, i, end, options);
    if (tok && !tok.quoted) {
      const upper = tok.value.toUpperCase();
      if (upper === 'STRAIGHT_JOIN') {
        // MySQL STRAIGHT_JOIN is a JOIN keyword, not just a prefix.
        const join = readJoinClause(ctx, tok.end, end, 'inner');
        if (!join) break;
        joins.push(join.clause);
        i = join.endOffset;
        prefixWords = [];
        continue;
      }
      if (upper === 'JOIN' || (upper === 'APPLY' && (prefixWords.includes('CROSS') || prefixWords.includes('OUTER')))) {
        const kind = inferJoinKind([...prefixWords, upper]);
        const natural = prefixWords.includes('NATURAL');
        prefixWords = [];
        const join = readJoinClause(ctx, tok.end, end, kind, natural);
        if (!join) break;
        joins.push(join.clause);
        i = join.endOffset;
        continue;
      }
      if (JOIN_PREFIXES.has(upper)) {
        prefixWords.push(upper);
        i = tok.end;
        continue;
      }
    }
    // Parenthesized FROM target (derived table) or identifier-led target.
    const item = readFromItem(ctx, i, end);
    if (!item) {
      // Defensive: advance one byte to avoid infinite loop on unexpected input.
      i = i + 1;
      continue;
    }
    fromItems.push(item.item);
    i = skipWsAndComments(source, item.endOffset, end, supportsHashComment);
    // Comma → another FROM item.
    if (source[i] === ',') {
      i = skipWsAndComments(source, i + 1, end, supportsHashComment);
      prefixWords = [];
      continue;
    }
  }

  return { fromItems, joins };
}

interface FromItemRead {
  readonly item: FromItem;
  readonly endOffset: number;
}

// eslint-disable-next-line complexity, max-lines-per-function
function readFromItem(
  ctx: SelectExtractCtx,
  start: number,
  end: number,
): FromItemRead | null {
  const { source, options, lineIdx, buffer } = ctx;
  const ctxSkip = skipContextFor(options);
  const i = skipWs(source, start, end);

  let target: FromItem['target'] | null = null;
  let bodyEnd = i;

  if (source[i] === '(') {
    // Derived table / parenthesized FROM expression.
    const close = skipParen(source, i, end, ctxSkip);
    const innerStart = i + 1;
    const innerEnd = close > 0 ? close - 1 : end;
    // Build a synthetic statement-like scope for the derived table by
    // recursing through extractQueryStructure on a faux StatementInfo.
    const subRange: SqlRange = rangeFromOffsets(lineIdx, innerStart, innerEnd);
    const subStmt = {
      id: `derived-${innerStart}-${innerEnd}`,
      range: subRange,
      kind: 'select' as const,
      risk: 'read' as const,
      readsFrom: [], writesTo: [], schemaEffects: [],
    };
    const subStructure = extractQueryStructure(buffer, subStmt);
    target = { kind: 'derived-table', subquery: subStructure };
    bodyEnd = close;
  } else {
    // Identifier-led: relation or function-call.
    const qn = readQualifiedName(source, i, end, options);
    if (!qn) return null;
    const j = qn.range.endOffset;
    if (source[j] === '(') {
      // Function-call FROM target.
      const close = skipParen(source, j, end, ctxSkip);
      const argText = source.slice(j + 1, close > 0 ? close - 1 : end).trim();
      target = {
        kind: 'function-call',
        routine: qn.qualified,
        args: argText ? [argText] : [],
      };
      bodyEnd = close;
    } else {
      // Detect CTE references — when the relation name (folded, unqualified)
      // matches a CTE declared in this statement's WITH clause, emit cte-ref
      // instead of relation. Schema-qualified targets always remain relations.
      const cte = qn.qualified.foldedSchema ? null : findCteByName(buffer, qn.qualified.foldedName);
      if (cte) target = { kind: 'cte-ref', cte };
      else
        target = {
          kind: 'relation',
          qualified: qn.qualified,
          nameRange: rangeFromOffsets(lineIdx, qn.terminalRange.startOffset, qn.terminalRange.endOffset),
          partCount: qn.partCount,
        };
      bodyEnd = j;
    }
  }
  // Optional AS-alias.
  let p = skipWs(source, bodyEnd, end);
  let aliasIdent = readIdentifier(source, p, end, options);
  let aliasEnd = bodyEnd;
  let alias: AliasBinding | undefined;
  if (aliasIdent && !aliasIdent.quoted && aliasIdent.value.toUpperCase() === 'AS') {
    p = skipWs(source, aliasIdent.end, end);
    aliasIdent = readIdentifier(source, p, end, options);
    // After AS, only accept a real identifier — a clause keyword would be
    // malformed SQL; null it out defensively.
    if (aliasIdent && !aliasIdent.quoted && (
      isClauseTerminator(aliasIdent.value)
      || JOIN_PREFIXES.has(aliasIdent.value.toUpperCase())
      || aliasIdent.value.toUpperCase() === 'JOIN'
      || aliasIdent.value.toUpperCase() === 'STRAIGHT_JOIN'
      || aliasIdent.value.toUpperCase() === 'ON'
      || aliasIdent.value.toUpperCase() === 'USING'
    )) aliasIdent = null;
  } else if (aliasIdent && !aliasIdent.quoted && (
    isClauseTerminator(aliasIdent.value)
    || JOIN_PREFIXES.has(aliasIdent.value.toUpperCase())
    || aliasIdent.value.toUpperCase() === 'JOIN'
    || aliasIdent.value.toUpperCase() === 'STRAIGHT_JOIN'
    || aliasIdent.value.toUpperCase() === 'ON'
    || aliasIdent.value.toUpperCase() === 'USING'
  )) {
    aliasIdent = null;
  }
  if (aliasIdent) {
    alias = {
      alias: aliasIdent.value,
      aliasFolded: foldIdentifier(aliasIdent.value, aliasIdent.quoted, options),
      target:
        target.kind === 'relation'
          ? target.qualified
          : { name: source.slice(start, bodyEnd).trim(), raw: source.slice(start, bodyEnd).trim(), quoted: false, foldedName: '' },
      source: target.kind === 'derived-table' ? 'derived-table' : 'from',
      range: rangeFromOffsets(lineIdx, aliasIdent.start, aliasIdent.end),
    };
    aliasEnd = aliasIdent.end;
  }

  return {
    item: {
      range: rangeFromOffsets(lineIdx, start, aliasEnd),
      ...(alias ? { alias } : {}),
      target,
    },
    endOffset: aliasEnd,
  };
}

interface JoinClauseRead {
  readonly clause: JoinClause;
  readonly endOffset: number;
}

// eslint-disable-next-line complexity
function readJoinClause(
  ctx: SelectExtractCtx,
  start: number,
  end: number,
  kind: JoinKind,
  natural = false,
): JoinClauseRead | null {
  const { source, options, lineIdx } = ctx;
  const i = skipWs(source, start, end);
  const right = readFromItem(ctx, i, end);
  if (!right) return null;
  let p = skipWs(source, right.endOffset, end);
  let on: readonly BooleanFragment[] | undefined;
  let using: readonly string[] | undefined;
  // ON predicate or USING (cols)
  const tok = readIdentifier(source, p, end, options);
  if (tok && !tok.quoted) {
    if (tok.value.toUpperCase() === 'ON') {
      const onStart = tok.end;
      const onEnd = findJoinClauseEnd(source, options, onStart, end);
      // Trim leading/trailing whitespace so the comparison parser sees a tight
      // range matching the historical `text.trim()` shape.
      let s = onStart;
      while (s < onEnd && /\s/.test(source[s])) s++;
      let e = onEnd;
      while (e > s && /\s/.test(source[e - 1])) e--;
      on = [buildBooleanFragment(source, options, lineIdx, s, e)];
      p = onEnd;
    } else if (tok.value.toUpperCase() === 'USING') {
      const usingStart = skipWsAndComments(source, tok.end, end, skipContextFor(options).supportsHashComment);
      if (source[usingStart] === '(') {
        const close = skipParen(source, usingStart, end, skipContextFor(options));
        const inner = source.slice(usingStart + 1, close > 0 ? close - 1 : end);
        using = inner.split(',').map((s) => s.trim()).filter(Boolean);
        p = close;
      }
    }
  }
  return {
    clause: {
      range: rangeFromOffsets(lineIdx, start, p),
      kind, right: right.item,
      ...(on ? { on } : {}),
      ...(using ? { using } : {}),
      ...(natural ? { natural } : {}),
    },
    endOffset: p,
  };
}

// Find the end of an ON predicate by scanning forward at depth 0 until we hit
// a JOIN-introducing keyword, a comma, or a clause terminator.
// eslint-disable-next-line complexity
function findJoinClauseEnd(
  source: string,
  options: ParseOptions,
  start: number,
  end: number,
): number {
  const ctx = skipContextFor(options);
  let depth = 0;
  let i = start;
  while (i < end) {
    const tok = readIdentifier(source, i, end, options);
    if (tok) {
      if (depth === 0 && !tok.quoted) {
        const u = tok.value.toUpperCase();
        if (u === 'JOIN' || u === 'STRAIGHT_JOIN' || JOIN_PREFIXES.has(u)) return tok.start;
        if (isClauseTerminator(u)) return tok.start;
      }
      i = tok.end; continue;
    }
    const c = source[i];
    if (c === '(') { depth++; i++; continue; }
    if (c === ')') { if (depth === 0) return i; depth--; i++; continue; }
    if (c === ',' && depth === 0) return i;
    // Skip strings/comments/quoted-ids
    const skipped = (function trySkip(): number {
      const c2 = source[i + 1] ?? '';
      if (c === '-' && c2 === '-') { let j = i + 2; while (j < end && source[j] !== '\n') j++; return j; }
      if (c === '/' && c2 === '*') { let j = i + 2; while (j < end && !(source[j] === '*' && source[j + 1] === '/')) j++; return j < end ? j + 2 : j; }
      if (c === "'") { let j = i + 1; while (j < end && source[j] !== "'") j++; return j < end ? j + 1 : j; }
      void ctx;
      return -1;
    })();
    if (skipped > i) { i = skipped; continue; }
    i++;
  }
  return end;
}

function inferJoinKind(words: readonly string[]): JoinKind {
  const has = (w: string): boolean => words.includes(w);
  if (has('CROSS') && has('APPLY')) return 'cross-apply';
  if (has('OUTER') && has('APPLY')) return 'outer-apply';
  if (has('LATERAL')) return 'lateral';
  if (has('CROSS')) return 'cross';
  if (has('FULL')) return 'full';
  if (has('LEFT')) return 'left';
  if (has('RIGHT')) return 'right';
  return 'inner';
}

function isClauseTerminator(value: string): boolean {
  const u = value.toUpperCase();
  return (
    u === 'WHERE' || u === 'GROUP' || u === 'ORDER' || u === 'HAVING'
    || u === 'LIMIT' || u === 'OFFSET' || u === 'FETCH' || u === 'WINDOW'
    || u === 'UNION' || u === 'INTERSECT' || u === 'EXCEPT'
    || u === 'RETURNING' || u === 'INTO'
  );
}

// Find a CTE binding declared in the same document by folded name. The CTE
// scope is computed from the document's leading WITH clause; a relation that
// shares its name with a CTE is treated as a cte-ref inside FROM.
function findCteByName(
  buffer: Parameters<typeof computeCteBindings>[0],
  foldedName: string,
): CteBinding | null {
  for (const stmt of buffer.documentStructure.statements) {
    const ctes = computeCteBindings(buffer, stmt.id);
    for (const c of ctes) {
      if (c.name.foldedName === foldedName) return c;
    }
  }
  return null;
}

function skipWs(source: string, start: number, end: number): number {
  let i = start;
  while (i < end && /\s/.test(source[i])) i++;
  return i;
}

// NEW-3: skip whitespace AND comments in the FROM-list tokenizer. Without
// this, a `--` / `#` / block comment between FROM and the relation (or
// between relations) is byte-walked by the defensive single-byte advance in
// `parseFromAndJoins`, turning the comment words into phantom relation/alias
// pairs. Mirrors the line/block-comment idiom used by `findJoinClauseEnd`;
// the `#` line comment is strictly MySQL-gated via `skipContextFor`
// (`supportsHashComment: engine === 'mysql'`). String / quoted-identifier
// skipping is intentionally NOT done here — a quoted `"x"` is a real relation.
function skipWsAndComments(
  source: string,
  start: number,
  end: number,
  supportsHashComment: boolean,
): number {
  let i = start;
  for (;;) {
    i = skipWs(source, i, end);
    if (i >= end) return i;
    const c = source[i];
    const c2 = source[i + 1] ?? '';
    if (c === '-' && c2 === '-') {
      let j = i + 2;
      while (j < end && source[j] !== '\n') j++;
      i = j;
      continue;
    }
    if (supportsHashComment && c === '#') {
      let j = i + 1;
      while (j < end && source[j] !== '\n') j++;
      i = j;
      continue;
    }
    if (c === '/' && c2 === '*') {
      let j = i + 2;
      while (j < end && !(source[j] === '*' && source[j + 1] === '/')) j++;
      i = j < end ? j + 2 : j;
      continue;
    }
    return i;
  }
}
